export const WEATHER_CONDITIONS = [
  'clear',
  'partly_cloudy',
  'cloudy',
  'rain',
] as const

export type DefaultWeatherCondition = (typeof WEATHER_CONDITIONS)[number]

export type DefaultWeatherDay = {
  day: string
  city: string
  temp_high_c: number
  temp_low_c: number
  condition: DefaultWeatherCondition
  precip_mm: number
}

const DEFAULT_CITIES = [
  { name: 'Riverton', high: 27 },
  { name: 'Cape Aldon', high: 22 },
  { name: 'Norbeck', high: 19 },
] as const

const DEFAULT_WEATHER_DAYS = 10
const DEFAULT_WEATHER_END_DAY = '2026-07-13'

function hashSeed(seed: string): number {
  let hash = 2166136261
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function createRandom(seed: string): () => number {
  let state = hashSeed(seed || 'peekie-weather') || 1
  return () => {
    state = Math.imul(state ^ (state >>> 15), 1 | state)
    state ^= state + Math.imul(state ^ (state >>> 7), 61 | state)
    return ((state ^ (state >>> 14)) >>> 0) / 4294967296
  }
}

function addDays(day: string, offset: number): string {
  const date = new Date(`${day}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) {
    return addDays(DEFAULT_WEATHER_END_DAY, offset)
  }
  date.setUTCDate(date.getUTCDate() + offset)
  return date.toISOString().slice(0, 10)
}

function randomInteger(random: () => number, min: number, max: number): number {
  return min + Math.floor(random() * (max - min + 1))
}

function generateCityWeather(
  seed: string,
  city: (typeof DEFAULT_CITIES)[number],
  endDay: string,
): DefaultWeatherDay[] {
  const random = createRandom(`${seed}:${city.name}`)
  return Array.from({ length: DEFAULT_WEATHER_DAYS }, (_, index) => {
    const condition =
      WEATHER_CONDITIONS[randomInteger(random, 0, WEATHER_CONDITIONS.length - 1)]!
    const high = city.high + randomInteger(random, -5, 5)
    const low = high - randomInteger(random, 7, 12)
    const precip_mm = condition === 'rain' ? randomInteger(random, 2, 18) : 0

    return {
      day: addDays(endDay, index - DEFAULT_WEATHER_DAYS + 1),
      city: city.name,
      temp_high_c: high,
      temp_low_c: low,
      condition,
      precip_mm,
    }
  })
}

export function generateDefaultWeather(
  seed: string,
  endDay = DEFAULT_WEATHER_END_DAY,
): DefaultWeatherDay[] {
  return DEFAULT_CITIES.flatMap((city) =>
    generateCityWeather(seed, city, endDay),
  )
}
